import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Alert, Nav } from "react-bootstrap";
import { FaGoogle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext.tsx";
import Header from "./Header.tsx";
import { palette, fonts } from "./style.tsx";

type Mode = "signIn" | "signUp" | "confirm" | "forgot" | "forgotSubmit";

const cardStyle: React.CSSProperties = {
  background: "rgba(255, 255, 255, 0.08)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  borderRadius: 16,
  padding: "32px 28px",
  maxWidth: 420,
  width: "100%",
  color: palette.textColor,
  fontFamily: fonts.baseFontFamily,
};

const labelStyle: React.CSSProperties = {
  color: palette.textColor,
  fontSize: "0.85rem",
  fontWeight: 600,
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  borderRadius: 10,
  background: "rgba(255, 255, 255, 0.95)",
  border: "none",
  padding: "10px 12px",
  color: "#000",
};

const primaryBtnStyle: React.CSSProperties = {
  width: "100%",
  backgroundColor: palette.secondaryColor,
  borderRadius: 50,
  border: "none",
  padding: "10px",
  color: palette.textColor,
  fontWeight: 700,
  marginTop: 12,
};

const linkStyle: React.CSSProperties = {
  color: palette.tertiaryColor,
  background: "none",
  border: "none",
  padding: 0,
  textDecoration: "underline",
  cursor: "pointer",
};

const LoginScreen: React.FC = () => {
  const { signIn, signUp, confirmSignUp, resendConfirmationCode, forgotPassword, forgotPasswordSubmit, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const clearMessages = () => {
    setError(null);
    setInfo(null);
  };

  const switchMode = (next: Mode) => {
    clearMessages();
    setMode(next);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate("/my-snitches");
    } catch (err: any) {
      if (err?.code === "UserNotConfirmedException") {
        setInfo("Your email isn't confirmed yet. Enter the code we sent.");
        setMode("confirm");
      } else {
        setError(err?.message || "Sign in failed.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await signUp(email, password);
      setInfo("We sent a confirmation code to your email.");
      setMode("confirm");
    } catch (err: any) {
      setError(err?.message || "Sign up failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setSubmitting(true);
    try {
      await confirmSignUp(email, code);
      setInfo("Email confirmed. Signing you in…");
      if (password) {
        await signIn(email, password);
        navigate("/my-snitches");
      } else {
        setMode("signIn");
      }
    } catch (err: any) {
      setError(err?.message || "Confirmation failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    clearMessages();
    try {
      await resendConfirmationCode(email);
      setInfo("A new code was sent.");
    } catch (err: any) {
      setError(err?.message || "Could not resend code.");
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setInfo("We sent a reset code to your email.");
      setMode("forgotSubmit");
    } catch (err: any) {
      setError(err?.message || "Could not start password reset.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setSubmitting(true);
    try {
      await forgotPasswordSubmit(email, code, newPassword);
      setInfo("Password reset. You can sign in now.");
      setMode("signIn");
      setPassword("");
    } catch (err: any) {
      setError(err?.message || "Could not reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    clearMessages();
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setError("Google sign-in isn't enabled yet — coming soon.");
    }
  };

  return (
    <Container fluid className="mt-4 px-3" style={{ paddingBottom: 40 }}>
      <Header onReset={() => navigate("/")} />
      <Row className="justify-content-center mt-4">
          <Col xs={12} className="d-flex justify-content-center">
            <div style={cardStyle}>
              {mode === "signIn" && (
                <>
                  <h3 style={{ marginBottom: 20, fontWeight: 700 }}>Welcome back</h3>
                  <Nav variant="underline" activeKey={mode} className="mb-3" onSelect={(k) => k && switchMode(k as Mode)}>
                    <Nav.Item><Nav.Link eventKey="signIn" style={{ color: palette.textColor }}>Sign In</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="signUp" style={{ color: palette.textColor }}>Sign Up</Nav.Link></Nav.Item>
                  </Nav>
                  {error && <Alert variant="danger" className="py-2">{error}</Alert>}
                  {info && <Alert variant="info" className="py-2">{info}</Alert>}
                  <Form onSubmit={handleSignIn}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Email</Form.Label>
                      <Form.Control type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Password</Form.Label>
                      <Form.Control type="password" required value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} />
                    </Form.Group>
                    <div className="text-end mb-2">
                      <button type="button" style={linkStyle} onClick={() => switchMode("forgot")}>Forgot password?</button>
                    </div>
                    <Button type="submit" style={primaryBtnStyle} disabled={submitting}>
                      {submitting ? "Signing in…" : "Sign In"}
                    </Button>
                  </Form>
                  <div className="text-center my-3" style={{ color: palette.accentColor }}>or</div>
                  <Button
                    onClick={handleGoogle}
                    style={{
                      ...primaryBtnStyle,
                      backgroundColor: "#fff",
                      color: "#333",
                      marginTop: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                    }}
                  >
                    <FaGoogle style={{ color: "#4285F4" }} />
                    Continue with Google
                  </Button>
                </>
              )}

              {mode === "signUp" && (
                <>
                  <h3 style={{ marginBottom: 20, fontWeight: 700 }}>Create your account</h3>
                  <Nav variant="underline" activeKey={mode} className="mb-3" onSelect={(k) => k && switchMode(k as Mode)}>
                    <Nav.Item><Nav.Link eventKey="signIn" style={{ color: palette.textColor }}>Sign In</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="signUp" style={{ color: palette.textColor }}>Sign Up</Nav.Link></Nav.Item>
                  </Nav>
                  {error && <Alert variant="danger" className="py-2">{error}</Alert>}
                  {info && <Alert variant="info" className="py-2">{info}</Alert>}
                  <Form onSubmit={handleSignUp}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Email</Form.Label>
                      <Form.Control type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Password</Form.Label>
                      <Form.Control type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} />
                      <Form.Text style={{ color: palette.accentColor }}>At least 8 characters.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Confirm Password</Form.Label>
                      <Form.Control type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={inputStyle} />
                    </Form.Group>
                    <Button type="submit" style={primaryBtnStyle} disabled={submitting}>
                      {submitting ? "Creating…" : "Create Account"}
                    </Button>
                  </Form>
                  <div className="text-center my-3" style={{ color: palette.accentColor }}>or</div>
                  <Button
                    onClick={handleGoogle}
                    style={{
                      ...primaryBtnStyle,
                      backgroundColor: "#fff",
                      color: "#333",
                      marginTop: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                    }}
                  >
                    <FaGoogle style={{ color: "#4285F4" }} />
                    Continue with Google
                  </Button>
                </>
              )}

              {mode === "confirm" && (
                <>
                  <h3 style={{ marginBottom: 20, fontWeight: 700 }}>Confirm your email</h3>
                  {error && <Alert variant="danger" className="py-2">{error}</Alert>}
                  {info && <Alert variant="info" className="py-2">{info}</Alert>}
                  <Form onSubmit={handleConfirm}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Email</Form.Label>
                      <Form.Control type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Confirmation Code</Form.Label>
                      <Form.Control type="text" required value={code} onChange={(e) => setCode(e.target.value)} style={inputStyle} />
                    </Form.Group>
                    <Button type="submit" style={primaryBtnStyle} disabled={submitting}>
                      {submitting ? "Confirming…" : "Confirm"}
                    </Button>
                  </Form>
                  <div className="text-center mt-3">
                    <button type="button" style={linkStyle} onClick={handleResend}>Resend code</button>
                    {" · "}
                    <button type="button" style={linkStyle} onClick={() => switchMode("signIn")}>Back to sign in</button>
                  </div>
                </>
              )}

              {mode === "forgot" && (
                <>
                  <h3 style={{ marginBottom: 20, fontWeight: 700 }}>Reset your password</h3>
                  {error && <Alert variant="danger" className="py-2">{error}</Alert>}
                  {info && <Alert variant="info" className="py-2">{info}</Alert>}
                  <Form onSubmit={handleForgot}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Email</Form.Label>
                      <Form.Control type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
                    </Form.Group>
                    <Button type="submit" style={primaryBtnStyle} disabled={submitting}>
                      {submitting ? "Sending…" : "Send Reset Code"}
                    </Button>
                  </Form>
                  <div className="text-center mt-3">
                    <button type="button" style={linkStyle} onClick={() => switchMode("signIn")}>Back to sign in</button>
                  </div>
                </>
              )}

              {mode === "forgotSubmit" && (
                <>
                  <h3 style={{ marginBottom: 20, fontWeight: 700 }}>Enter your new password</h3>
                  {error && <Alert variant="danger" className="py-2">{error}</Alert>}
                  {info && <Alert variant="info" className="py-2">{info}</Alert>}
                  <Form onSubmit={handleForgotSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Email</Form.Label>
                      <Form.Control type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Reset Code</Form.Label>
                      <Form.Control type="text" required value={code} onChange={(e) => setCode(e.target.value)} style={inputStyle} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>New Password</Form.Label>
                      <Form.Control type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={inputStyle} />
                    </Form.Group>
                    <Button type="submit" style={primaryBtnStyle} disabled={submitting}>
                      {submitting ? "Resetting…" : "Set New Password"}
                    </Button>
                  </Form>
                </>
              )}
            </div>
        </Col>
      </Row>
    </Container>
  );
};

export default LoginScreen;
